import { datadogRum } from '@datadog/browser-rum';
import { getIsDevelopment } from '../constants/environment';
import { toReportableError } from './affordaPortalErrors';

/**
 * Check if Datadog RUM is initialized and available
 * @returns {boolean}
 */
const isDatadogRumAvailable = () => {
  return (
    typeof datadogRum !== 'undefined' &&
    datadogRum &&
    typeof datadogRum.addAction === 'function'
  );
};

/**
 * Sanitize endpoint path for use in Datadog timing names
 * Removes UUIDs, query parameters, and creates a readable, valid timing name
 * @param {string} endpoint - The API endpoint path
 * @returns {string} - Sanitized endpoint suitable for timing names
 */
const sanitizeEndpointForTiming = (endpoint) => {
  if (!endpoint) return 'unknown';

  // Remove query parameters (everything after ?)
  const pathOnly = endpoint.split('?')[0];

  // UUID regex pattern
  const uuidPattern =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

  // Remove leading/trailing slashes
  let sanitized = pathOnly.replace(/^\/+|\/+$/g, '');

  // Split by slash to get path segments
  const segments = sanitized.split('/');

  // Filter out UUID segments and keep meaningful resource names
  const meaningfulSegments = segments.filter((segment) => {
    // Keep segment if it's not a UUID
    return !uuidPattern.test(segment);
  });

  // If we have meaningful segments, join them with dots
  if (meaningfulSegments.length > 0) {
    return meaningfulSegments.join('.');
  }

  // Fallback: if all segments were UUIDs, use a generic name
  return 'resource';
};

/**
 * Tracks affordaPortal page views
 * @param {string} pageName - The name of the page
 * @param {Object} attributes - Additional attributes for the page view
 */
export const trackAffordaPortalPageView = (pageName, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackAffordaPortalPageView');
    return;
  }

  datadogRum.addAction(`afforda.portal.page-view.${pageName}`, {
    timestamp: Date.now(),
    pageName,
    ...attributes,
  });
};

/**
 * Tracks section progress updates
 * @param {string} sectionName - The name of the section
 * @param {string} status - The status ('started', 'in-progress', 'completed', 'skipped')
 * @param {Object} attributes - Additional attributes
 */
export const trackSectionProgress = (sectionName, status, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackSectionProgress');
    return;
  }

  datadogRum.addAction(
    `afforda.portal.section-progress.${sectionName}.${status}`,
    {
      sectionName,
      status,
      timestamp: Date.now(),
      ...attributes,
    },
  );
};

/**
 * Tracks form submissions
 * @param {string} formName - The name of the form
 * @param {boolean} success - Whether the submission was successful
 * @param {Object} attributes - Additional attributes
 */
export const trackFormSubmission = (formName, success, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackFormSubmission');
    return;
  }

  datadogRum.addAction('afforda.portal.form-submission', {
    formName,
    success,
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks form validation errors
 * @param {string} formName - The name of the form
 * @param {Object} errors - The validation errors object
 * @param {Object} attributes - Additional attributes
 */
export const trackFormValidationError = (formName, errors, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackFormValidationError');
    return;
  }

  datadogRum.addAction('afforda.portal.form-validation-error', {
    formName,
    errorCount: Object.keys(errors).length,
    errors: JSON.stringify(errors),
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks navigation between sections
 * @param {string} from - The origin section/page
 * @param {string} to - The destination section/page
 * @param {Object} attributes - Additional attributes
 */
export const trackNavigation = (from, to, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackNavigation');
    return;
  }

  datadogRum.addAction('afforda.portal.navigation', {
    from,
    to,
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks user actions (clicks, interactions)
 * @param {string} actionName - The name of the action
 * @param {Object} attributes - Additional attributes
 */
export const trackUserAction = (actionName, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackUserAction');
    return;
  }

  if (getIsDevelopment()) {
    console.log(`[USER_ACTION] ${actionName}`, attributes);
  }

  datadogRum.addAction(`afforda.portal.user-action.${actionName}`, {
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks API calls with trace context for backend correlation
 * @param {string} name - The name of the API call
 * @param {string} endpoint - The API endpoint
 * @param {string} method - The HTTP method
 * @param {boolean} success - Whether the call was successful
 * @param {number} duration - The duration in milliseconds
 * @param {Object} attributes - Additional attributes
 */
export const trackApiCall = (
  name,
  endpoint,
  method,
  success,
  duration,
  attributes = {},
) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackApiCall');
    return;
  }

  // Get current trace context from Datadog RUM
  const rumContext = datadogRum.getInternalContext();

  datadogRum.addAction(`afforda.portal.api-call.${name}`, {
    endpoint,
    method,
    success,
    duration,
    timestamp: Date.now(),

    // Add trace context for backend correlation
    'trace.id': rumContext?.application_id,
    'span.id': rumContext?.session_id,
    'view.id': rumContext?.view?.id,
    'session.id': rumContext?.session_id,

    ...attributes,
  });
};

/**
 * Start tracking an API resource (call before API request)
 * @param {string} endpoint - The API endpoint
 * @param {string} method - The HTTP method
 * @returns {Object} - Resource tracking context
 */
export const startApiResourceTracking = (endpoint, method) => {
  if (!isDatadogRumAvailable()) {
    return { startTime: Date.now() };
  }

  const rumContext = datadogRum.getInternalContext();

  return {
    startTime: Date.now(),
    endpoint,
    method,
    traceId: rumContext?.application_id,
    spanId: rumContext?.session_id,
    viewId: rumContext?.view?.id,
  };
};

/**
 * Complete tracking an API resource (call after API response)
 * @param {Object} trackingContext - Context from startApiResourceTracking
 * @param {boolean} success - Whether the call was successful
 * @param {Object} attributes - Additional attributes
 */
export const completeApiResourceTracking = (
  trackingContext,
  success,
  attributes = {},
) => {
  if (!isDatadogRumAvailable()) {
    return;
  }

  const duration = Date.now() - trackingContext.startTime;

  // Track as both an action and a custom timing
  datadogRum.addAction('afforda.portal.api-call', {
    endpoint: trackingContext.endpoint,
    method: trackingContext.method,
    success,
    duration,
    'trace.id': trackingContext.traceId,
    'span.id': trackingContext.spanId,
    'view.id': trackingContext.viewId,
    timestamp: Date.now(),
    ...attributes,
  });

  // Also add as timing for better correlation
  // Sanitize endpoint to create valid timing name without UUIDs
  const sanitizedEndpoint = sanitizeEndpointForTiming(trackingContext.endpoint);
  datadogRum.addTiming(`api.${trackingContext.method}.${sanitizedEndpoint}`, {
    duration,
    success,
    ...attributes,
  });
};

/**
 * Tracks errors with context
 * @param {Error|string} error - The error object or message
 * @param {string} context - The context where the error occurred
 * @param {Object} attributes - Additional attributes
 */
/** Set on errors already sent via trackError so outer catches can skip duplicate RUM events. */
const AFFORDA_PORTAL_ERROR_TRACKED = Symbol.for(
  'fortress.affordaPortal.errorTracked',
);

export const markAffordaPortalErrorTracked = (error) => {
  if (error && typeof error === 'object') {
    error[AFFORDA_PORTAL_ERROR_TRACKED] = true;
  }
};

export const isAffordaPortalErrorTracked = (error) =>
  Boolean(
    error && typeof error === 'object' && error[AFFORDA_PORTAL_ERROR_TRACKED],
  );

export const trackError = (error, context, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackError');
    return;
  }

  const reportableError =
    error && typeof error === 'object' ? toReportableError(error) : error;

  datadogRum.addError(reportableError, {
    context: `afforda.portal.${context}`,
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks document uploads
 * @param {string} documentType - The type of document being uploaded
 * @param {boolean} success - Whether the upload was successful
 * @param {Object} attributes - Additional attributes
 */
export const trackDocumentUpload = (documentType, success, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackDocumentUpload');
    return;
  }

  datadogRum.addAction('afforda.portal.document-upload', {
    documentType,
    success,
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks application milestones
 * @param {string} milestone - The milestone name
 * @param {Object} attributes - Additional attributes
 */
export const trackMilestone = (milestone, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackMilestone');
    return;
  }

  if (getIsDevelopment()) {
    console.log(`[MILESTONE] ${milestone}`, attributes);
  }

  datadogRum.addAction(`afforda.portal.milestone.${milestone}`, {
    milestone,
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks timing for custom metrics
 * @param {string} name - The name of the timing metric
 * @param {number} duration - The duration in milliseconds
 * @param {Object} attributes - Additional attributes
 */
export const trackTiming = (name, duration, attributes = {}) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackTiming');
    return;
  }

  datadogRum.addAction('afforda.portal.timing', {
    name,
    duration,
    timestamp: Date.now(),
    ...attributes,
  });
};

/**
 * Tracks network/API errors with enhanced context
 * @param {Error} error - The Axios error object
 * @param {string} endpoint - The API endpoint
 * @param {string} method - The HTTP method
 * @param {Object} additionalContext - Additional context
 */
export const trackNetworkError = (
  error,
  endpoint,
  method,
  additionalContext = {},
) => {
  if (!isDatadogRumAvailable()) {
    console.warn('Datadog RUM not available for trackNetworkError');
    return;
  }

  // Determine error type
  let errorType = 'unknown';
  let errorCode = null;
  let errorMessage = error?.message || 'Unknown error';
  let statusCode = null;
  let responseData = null;
  let validationDetails = null;

  // Check if it's an Axios error
  if (error.isAxiosError) {
    errorCode = error.code; // ECONNABORTED, ERR_NETWORK, etc.

    if (error.response) {
      // Server responded with error status
      errorType = 'response_error';
      statusCode = error.response.status;
      responseData = error.response.data;

      // Enhanced error message extraction for validation errors
      if (statusCode === 400) {
        errorType = 'validation_error';

        // Try multiple common error message formats from backend
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error?.message ||
          error.response.data?.error ||
          (typeof error.response.data === 'string'
            ? error.response.data
            : null) ||
          errorMessage;

        // Extract validation details if available
        validationDetails = {
          validation: error.response.data?.validation,
          details: error.response.data?.details,
          invalidFields: error.response.data?.invalidFields,
        };
      } else {
        errorMessage = error.response.data?.message || errorMessage;
      }
    } else if (error.request) {
      // Request made but no response received
      errorType = 'network_error';

      // Differentiate network error types
      if (error.code === 'ECONNABORTED') {
        errorType = 'timeout_error';
      } else if (error.code === 'ERR_NETWORK') {
        errorType = 'connection_error';
      } else if (error.message.includes('CORS')) {
        errorType = 'cors_error';
      }
    } else {
      // Error setting up the request
      errorType = 'request_setup_error';
    }
  }

  // Sanitize response data (remove sensitive fields and limit size)
  const sanitizedResponseData = responseData
    ? JSON.stringify(responseData).substring(0, 1000) // Increased from 500 to 1000 chars
    : null;

  // Build comprehensive error context
  const errorContext = {
    context: 'afforda.portal.network-error',
    errorType,
    errorCode,
    errorMessage,
    endpoint,
    method: method?.toUpperCase(),
    statusCode,
    responseDataPreview: sanitizedResponseData,
    validationDetails,

    // Axios-specific details
    isAxiosError: Boolean(error.isAxiosError),
    hasResponse: Boolean(error.response),
    hasRequest: Boolean(error.request),

    // Request config (sanitized)
    requestUrl: error.config?.url,
    requestMethod: error.config?.method,
    requestTimeout: error.config?.timeout,

    // Additional context
    ...additionalContext,

    timestamp: Date.now(),
  };
  const classificationCategory = errorContext.errorCategory || 'unclassified';
  const classificationReason = errorContext.errorReason || errorType;
  const classificationMessageId = errorContext.errorMessageId || 'none';

  // Skip tracking authentication errors (401) and expired token errors
  // These are expected flows, not actual errors
  const isAuthError = statusCode === 401;
  const isExpiredTokenError =
    errorMessage?.includes('Authentication Token has expired') ||
    errorMessage?.includes('Missing authentication');

  if (isAuthError || isExpiredTokenError) {
    // Log for debugging in development only, but don't send to Datadog
    if (
      process.env.REACT_APP_NODE_ENV === 'development' ||
      process.env.REACT_APP_NODE_ENV === 'ephemeral' ||
      process.env.NODE_ENV === 'development'
    ) {
      console.group('⚠️ Authentication Error (not sent to Datadog)');
      console.warn('Error Classification:', classificationCategory);
      console.warn('Error Reason:', classificationReason);
      console.warn('Status Code:', statusCode);
      console.warn('Error Message:', errorMessage);
      console.warn('Endpoint:', endpoint);
      console.groupEnd();
    }
    return; // Skip sending to Datadog
  }

  // NEW: Console log for debugging in development
  if (
    process.env.REACT_APP_NODE_ENV === 'development' ||
    process.env.REACT_APP_NODE_ENV === 'ephemeral' ||
    process.env.NODE_ENV === 'development'
  ) {
    console.group('🔴 Datadog Network Error Tracked');
    console.error('Original Error:', error);
    console.log('Error Classification:', classificationCategory);
    console.log('Error Reason:', classificationReason);
    console.log('Error Message ID:', classificationMessageId);
    console.log('Legacy Error Type:', errorType);
    console.log('Status Code:', statusCode);
    console.log('Error Message:', errorMessage);
    console.log('Endpoint:', endpoint);
    console.log('Response Data:', responseData);
    console.log('Full Context:', errorContext);
    console.groupEnd();
  }

  // Ensure errorMessage is a string
  if (typeof errorMessage !== 'string') {
    errorMessage = String(errorMessage || 'Unknown error');
  }
  // Create a new Error object with the enhanced error message
  // This ensures Datadog gets a proper error message instead of the generic Axios message
  const enhancedError = new Error(errorMessage);

  // Preserve the original stack trace if available
  if (error?.stack) {
    enhancedError.stack = error.stack;
  }

  // Add additional properties to the error object for better tracking
  enhancedError.name = `${errorType}_${statusCode || 'unknown'}`;
  enhancedError.originalError = error;
  enhancedError.isAxiosError = error?.isAxiosError;

  // Track as error in Datadog with the enhanced error object
  datadogRum.addError(enhancedError, errorContext);
};
